import * as React from "react";
import { PsycheRadarLabelLayer } from "./LabelLayer/PsycheRadarLabelLayer";
import { PsycheRadarSpokeLayers } from "./SpokeLayers/PsycheRadarSpokeLayers";

function PolarToCartesian(
    cx: number,
    cy: number,
    radius: number,
    angleDeg: number
) {
    const angleInRadians = ((angleDeg - 90) * Math.PI) / 180;

    return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
    };
}

export function PsycheRadarSvg({ controller }: { controller: any }) {
    const {
        size,
        centerX,
        centerY,
        outerRadius,
        viewBoxTop,
        viewBoxHeight,
        centerDiscRadius,
        isCenterHovered,
        isCenterHoverGlowArmed,
        isCenterHoverGlowVisible,
        centerDiscFill,
        isCenterHiddenByCheckbox,
        setIsCenterHovered,
        setSelectedIndex,
        isTouchZoomLocked,
    } = controller;

    return (
        <svg
            ref={controller.svgRef}
            viewBox={`0 ${viewBoxTop} ${size} ${viewBoxHeight}`}
            width="100%"
            role="img"
            aria-label="Psyche under attack radar diagram"
            style={{
                touchAction: "pinch-zoom",
                display: "block",
                height: "auto",
                pointerEvents: isTouchZoomLocked ? "none" : "auto",
            }}
            onPointerDownCapture={controller.handleRadarPointerDown}
            onPointerDown={controller.handleRadarPointerDown}
        >
            <defs>
                <radialGradient id="psyche-glow" cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor="#fff1c1" stopOpacity="0.3" />
                    <stop offset="65%" stopColor="#3b2c20" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#140f0b" stopOpacity="0" />
                </radialGradient>
                <radialGradient
                    id="center-hover-glow"
                    gradientUnits="userSpaceOnUse"
                    cx={centerX}
                    cy={centerY}
                    r={outerRadius + 18}
                >
                    <stop offset="0%" stopColor="#f7dc9f" stopOpacity="0.7" />
                    <stop offset="18%" stopColor="#f7dc9f" stopOpacity="0.6" />
                    <stop offset="38%" stopColor="#f5d28f" stopOpacity="0.48" />
                    <stop offset="58%" stopColor="#f5d28f" stopOpacity="0.36" />
                    <stop offset="74%" stopColor="#f5d28f" stopOpacity="0.24" />
                    <stop offset="88%" stopColor="#f5d28f" stopOpacity="0.14" />
                    <stop offset="96%" stopColor="#f5d28f" stopOpacity="0.055" />
                    <stop offset="100%" stopColor="#f5d28f" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="attack-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffd166" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#ef476f" stopOpacity="0.95" />
                </linearGradient>
                <filter id="soft-shadow" x="-40%" y="-40%" width="180%" height="180%">
                    <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#000000" floodOpacity="0.25" />
                </filter>
                <filter id="center-pulse-glow" x="-260%" y="-260%" width="620%" height="620%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="16" />
                </filter>
                <filter id="impact-flare-glow" x="-220%" y="-220%" width="440%" height="440%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="3.4" />
                </filter>
            </defs>

            <rect width={size} height={size} fill="rgba(18, 15, 13, 0.05)" rx="32" />
            <circle cx={centerX} cy={centerY} r={outerRadius} fill="url(#psyche-glow)" opacity="0.75" />

            {[0.34, 0.5, 0.66, 0.8, 0.92, 1].map((scale) => (
                <circle
                    key={scale}
                    cx={centerX}
                    cy={centerY}
                    r={outerRadius * scale}
                    fill="none"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={scale < 0.7 ? "1.5" : "2"}
                />
            ))}

            {!isCenterHiddenByCheckbox && (
                <g pointerEvents="none">
                    <circle
                        cx={centerX}
                        cy={centerY}
                        r={outerRadius + 18}
                        fill="url(#center-hover-glow)"
                        opacity={isCenterHoverGlowArmed ? 0.62 : 0.18}
                        style={{ transition: "opacity 1000ms ease" }}
                    />
                    {isCenterHoverGlowVisible ? (
                        <circle
                            cx={centerX}
                            cy={centerY}
                            r={outerRadius + 18}
                            fill="url(#center-hover-glow)"
                            opacity={isCenterHoverGlowArmed ? "0.22" : "0.06"}
                        >
                            {isCenterHoverGlowArmed ? (
                                <animate attributeName="opacity" values="0.16;0.3;0.16" dur="4s" repeatCount="indefinite" />
                            ) : null}
                        </circle>
                    ) : null}
                </g>
            )}

            {!isCenterHiddenByCheckbox && (
                <circle
                    cx={centerX}
                    cy={centerY}
                    r={centerDiscRadius + 20}
                    fill="#f5d28f"
                    opacity={isCenterHovered ? 0.18 : 0.08}
                    filter="url(#center-pulse-glow)"
                    data-radar-interactive="true"
                    style={{ cursor: isTouchZoomLocked ? "default" : "pointer" }}
                    onPointerEnter={() => setIsCenterHovered(true)}
                    onPointerLeave={() => setIsCenterHovered(false)}
                    onPointerDown={(event) => {
                        if (isTouchZoomLocked) {
                            return;
                        }

                        event.stopPropagation();
                        setSelectedIndex(null);
                    }}
                >
                    <animate
                        attributeName="opacity"
                        values={isCenterHovered ? "0.16;0.28;0.16" : "0.08;0.14;0.08"}
                        dur={isCenterHovered ? "4.4s" : "5.2s"}
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="r"
                        values={
                            isCenterHovered
                                ? `${centerDiscRadius + 28};${centerDiscRadius + 48};${centerDiscRadius + 28}`
                                : `${centerDiscRadius + 14};${centerDiscRadius + 36};${centerDiscRadius + 14}`
                        }
                        dur="3.6s"
                        repeatCount="indefinite"
                    />
                </circle>
            )}

            <PsycheRadarSpokeLayers controller={controller} />
            <PsycheRadarLabelLayer controller={controller} />

            {!isCenterHiddenByCheckbox && (
                    <circle
                    cx={centerX}
                    cy={centerY}
                    r={centerDiscRadius}
                    fill={centerDiscFill}
                    stroke="none"
                    data-radar-interactive="true"
                    style={{ cursor: isTouchZoomLocked ? "default" : "pointer", fill: centerDiscFill, transition: "fill 70ms ease" }}
                    onPointerEnter={() => setIsCenterHovered(true)}
                    onPointerLeave={() => setIsCenterHovered(false)}
                    onPointerDown={(event) => {
                        if (isTouchZoomLocked) {
                            return;
                        }

                        event.stopPropagation();
                        setSelectedIndex(null);
                    }}
                />
            )}
        </svg>
    );
}
